export class WebRTCConnectionManager {
    constructor(config = {}) {
        // 配置参数
        this.config = {
            timeout: 20000,       // 默认超时时间(ms)
            maxRetries: 2,        // 最大重试次数
            retryDelay: 3000,     // 重试延迟(ms)
            ...config
        };

        // 连接状态跟踪
        this.pendingConnections = new Map(); // 存储待建立的连接
        this.activeConnections = new Map();  // 存储已建立的连接
    }

    // 创建新连接并发送offer
    async connect_to_user(userId, fromUserId, socket, localStream) {
        if (this.pendingConnections.has(userId) || this.activeConnections.has(userId)) {
            return;
        }

        try {
            // 创建RTCPeerConnection实例
            const pc = new RTCPeerConnection(this.config.rtcConfig);

            localStream.getAudioTracks().forEach(track => {
                pc.addTrack(track, localStream);
            });

            // 设置流媒体处理
            pc.ontrack = (event) => {
                if (this.config.onTrack) {
                    this.config.onTrack(event.streams[0]);
                }
            };

            // 设置ICE候选者处理
            pc.onicecandidate = (event) => {
                if (event.candidate) {
                    socket.emit('send-candidate', {
                        userId: userId,
                        candidate: event.candidate,
                        fromUserId: fromUserId
                    });
                }
            };

            // 创建待处理连接记录
            const connectionRecord = {
                pc,
                userId,
                retryCount: 0,
                timeoutId: null,
                state: 'connecting'
            };

            // 添加到待处理连接列表
            this.pendingConnections.set(userId, connectionRecord);

            // 创建并发送offer
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);

            // 通过信令通道发送offer
            socket.emit('send-offer', {
                userId: userId,
                fromUserId: fromUserId,
                offer: offer
            });

            // 设置超时监控
            this._startConnectionTimer(userId);

            // 设置连接状态变化监听
            this._setupConnectionListeners(userId, pc);

            return pc;
        } catch (error) {
            console.error(`创建连接失败 (${userId}):`, error);
            this._handleConnectionFailure(userId, 'create_offer_failed');
            return null;
        }
    }

    // 处理接收到的offer
    async handleOffer(offerData, socket, localStream) {
        const {offer, fromUserId, userId} = offerData;

        try {
            // 创建RTCPeerConnection实例
            const pc = new RTCPeerConnection(this.config.rtcConfig);

            localStream.getAudioTracks().forEach(track => {
                pc.addTrack(track, localStream);
            });


            // 设置流媒体处理
            pc.ontrack = (event) => {
                if (this.config.onTrack) {
                    this.config.onTrack(event.streams[0]);
                }
            };

            // 设置ICE候选者处理
            pc.onicecandidate = (event) => {
                if (event.candidate) {
                    socket.emit('send-candidate', {
                        userId: fromUserId,
                        candidate: event.candidate,
                        fromUserId: userId
                    });
                }
            };

            // 创建待处理连接记录
            const connectionRecord = {
                pc,
                userId: fromUserId,
                retryCount: 0,
                timeoutId: null,
                state: 'connecting'
            };

            // 添加到待处理连接列表
            this.pendingConnections.set(fromUserId, connectionRecord);

            // 设置远程描述
            await pc.setRemoteDescription(new RTCSessionDescription(offer));

            // 创建并发送answer
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);

            // 设置连接状态变化监听
            this._setupConnectionListeners(fromUserId, pc);

            socket.emit('send-answer', {
                userId: fromUserId,
                answer: answer,
                fromUserId: userId
            });
        } catch (error) {
            console.error(`处理offer失败 (${fromUserId}):`, error);
            this._handleConnectionFailure(fromUserId, 'process_offer_failed');
            throw error;
        }
    }

    // 处理接收到的answer
    handleAnswer(fromUserId, answer) {
        const connection = this.pendingConnections.get(fromUserId);

        if (!connection) {
            console.log(`收到未知用户 ${fromUserId} 的answer`);
            return;
        }

        // 清除超时计时器
        clearTimeout(connection.timeoutId);

        try {
            // 设置远程描述
            connection.pc.setRemoteDescription(new RTCSessionDescription(answer))
                .then(() => {
                    // 连接成功，从待处理移至活跃连接
                    this.activeConnections.set(fromUserId, connection);
                    this.pendingConnections.delete(fromUserId);
                })
                .catch(error => {
                    console.error(`设置远程描述失败 (${fromUserId}):`, error);
                    this._handleConnectionFailure(fromUserId, 'set_answer_failed');
                });
        } catch (error) {
            this._handleConnectionFailure(fromUserId, 'process_answer_failed');
        }
    }

    // 处理接收到的ICE候选者
    async handleCandidate(fromUserId, userId, candidate, socket) {
        const connection = this.pendingConnections.get(fromUserId) || this.activeConnections.get(fromUserId);

        if (!connection) {
            console.warn(`收到未知用户 ${fromUserId} 的ICE候选者`);
            return;
        }

        try {
            await connection.pc.addIceCandidate(new RTCIceCandidate(candidate));
            this._setupAudioStreamProcessing(connection.pc, userId, socket)
        } catch (error) {
            console.error(`添加ICE候选者失败 (${fromUserId}):`, error);
            console.error(`添加ICE候选者失败 (${fromUserId}):`, candidate);
            this._handleConnectionFailure(fromUserId, 'add_ice_candidate_failed');
        }
    }

    // 启动连接超时计时器
    _startConnectionTimer(userId) {
        const connection = this.pendingConnections.get(userId);

        if (!connection) return;

        // 设置超时计时器
        connection.timeoutId = setTimeout(() => {
            this._handleConnectionTimeout(userId);
        }, this.config.timeout);
    }

    // 处理连接超时
    _handleConnectionTimeout(userId) {
        const connection = this.pendingConnections.get(userId);

        if (!connection) return;

        console.warn(`连接超时 (${userId})`);

        // 检查是否可以重试
        if (connection.retryCount < this.config.maxRetries) {
            connection.retryCount++;
            console.warn(`尝试重新连接 (${connection.retryCount}/${this.config.maxRetries})`);

            // 清除当前连接
            this._cleanupConnection(connection);

            // 延迟后重试
            setTimeout(() => {
                this.connect_to_user(userId, connection.signalingChannel);
            }, this.config.retryDelay);
        } else {
            // 达到最大重试次数，标记为失败
            this._handleConnectionFailure(userId, 'connection_timeout');
        }
    }

    // 处理连接失败
    _handleConnectionFailure(userId, reason) {
        const connection = this.pendingConnections.get(userId);

        if (!connection) return;

        console.error(`连接失败 (${userId}): ${reason}`);

        // 清理资源
        this._cleanupConnection(connection);

        // 从待处理列表移除
        this.pendingConnections.delete(userId);

        // 触发失败回调
        if (this.config.onConnectionFailed) {
            this.config.onConnectionFailed(userId, reason);
        }
    }

    // 清理连接资源
    _cleanupConnection(connection) {
        // 清除超时计时器
        if (connection.timeoutId) {
            clearTimeout(connection.timeoutId);
        }

        // 关闭RTCPeerConnection
        if (connection.pc && connection.pc.signalingState !== 'closed') {
            connection.pc.close();
        }

        // 释放引用，帮助垃圾回收
        connection.pc = null;
    }

    // 设置连接状态监听器
    _setupConnectionListeners(userId, pc) {
        // 监听连接状态变化
        pc.addEventListener('connectionstatechange', () => {
            console.warn(`用户 ${userId} 连接状态变化: ${pc.connectionState}`);

            if (pc.connectionState === 'connected') {
                // 连接成功
                const connection = this.pendingConnections.get(userId);
                if (connection) {
                    this.activeConnections.set(userId, connection);
                    this.pendingConnections.delete(userId);
                }
            } else if (pc.connectionState === 'failed' ||
                pc.connectionState === 'disconnected' ||
                pc.connectionState === 'closed') {
                // 连接失败或断开
                this._handleConnectionFailure(userId, `connection_state_${pc.connectionState}`);
            }
        });
    }

    // 在连接成功后启动音频流处理
    _setupAudioStreamProcessing(pc, userId, socket) {
        const audioTrack = pc.getReceivers().find(r => r.track.kind === 'audio').track;
        const audioStream = new MediaStream([audioTrack]);

        // 动态检测支持的音频格式（按优先级排序）
        const supportedFormats = [
            'audio/webm; codecs=opus',    // Chrome/Firefox
            'audio/ogg; codecs=opus',     // Firefox
            'audio/webm',                 // 默认fallback
            'audio/mp4'                   // Safari
        ];

        // 寻找第一个支持的格式
        const selectedFormat = supportedFormats.find(format =>
            MediaRecorder.isTypeSupported(format)
        );

        if (!selectedFormat) {
            console.error('当前浏览器不支持任何音频录制格式');
            return;
        }

        // 创建MediaRecorder实例
        this.audioStreamProcessor = new MediaRecorder(audioStream, {
            mimeType: selectedFormat,
            audioBitsPerSecond: 64000
        });

        // 设置数据可用时的处理
        this.audioStreamProcessor.ondataavailable = (event) => {
            if (event.data.size > 0) {
                this._sendAudioChunk(event.data, userId, socket);
            }
        };

        // 每200ms收集一次数据
        this.audioStreamProcessor.start(200);
    }

    // 通过WebSocket发送音频数据
    _sendAudioChunk(chunk, userId, socket) {
        const reader = new FileReader();
        // 需要在这整理（通过activeConnections）出已建立连接的所有用户id，包括自己（userId）
        const toUsersId = Object.keys(this.activeConnections).concat(userId);
        reader.onload = () => {
            const arrayBuffer = reader.result;
            socket.emit('send-audio', {
                toUsersId: toUsersId,
                audioData: arrayBuffer,
            });
        };
        reader.readAsArrayBuffer(chunk);
    }


    // 关闭与特定用户的连接
    closeConnection(userId) {
        // 检查待处理连接
        if (this.pendingConnections.has(userId)) {
            const connection = this.pendingConnections.get(userId);
            this._cleanupConnection(connection);
            this.pendingConnections.delete(userId);
            return true;
        }

        // 检查活跃连接
        if (this.activeConnections.has(userId)) {
            const connection = this.activeConnections.get(userId);
            this._cleanupConnection(connection);
            this.activeConnections.delete(userId);
            return true;
        }

        if (this.audioStreamProcessor) {
            this.audioStreamProcessor.stop();
            this.audioStreamProcessor = null;
        }

        return false;
    }

    // 关闭所有连接
    closeAllConnections() {
        // 关闭所有待处理连接
        this.pendingConnections.forEach(connection => {
            this._cleanupConnection(connection);
        });
        this.pendingConnections.clear();

        // 关闭所有活跃连接
        this.activeConnections.forEach(connection => {
            this._cleanupConnection(connection);
        });
        this.activeConnections.clear();
    }
}